import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Crown, Loader2 } from "lucide-react";
import { resolveImageUrl } from "@/lib/api";

export default function TransferOwnershipModal({ isOpen, onClose, members, currentUserId, onTransfer }) {
  const [selectedMember, setSelectedMember] = useState("");
  const [loading, setLoading] = useState(false);

  const eligibleMembers = members.filter(m => m.user_id !== currentUserId);

  const handleTransfer = async () => {
    if (!selectedMember) {
      toast.error("Please select a member");
      return;
    }

    setLoading(true);
    try {
      await onTransfer(selectedMember);
      onClose();
    } catch {
      // Error handled in parent
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
            Transfer Ownership
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm text-[#6B665E] font-['Nunito'] mb-4">
            Select a member to transfer group ownership to. They will become the new creator and admin.
          </p>
          
          <Select value={selectedMember} onValueChange={setSelectedMember}>
            <SelectTrigger className="border-[#E5E0D5] font-['Nunito']">
              <SelectValue placeholder="Select a member" />
            </SelectTrigger>
            <SelectContent className="bg-white max-h-60">
              {eligibleMembers.map(member => (
                <SelectItem key={member.user_id} value={member.user_id} className="font-['Nunito']">
                  <span className="flex items-center gap-2">
                    <Avatar className="w-6 h-6">
                      <AvatarImage src={resolveImageUrl(member.picture)} />
                      <AvatarFallback className="text-xs bg-[var(--liturgical-primary)] text-white">
                        {member.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    {member.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E0D5]">Cancel</Button>
          <Button onClick={handleTransfer} disabled={loading || !selectedMember} className="bg-[#D4AF37] hover:bg-[#B8972F] text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Crown className="w-4 h-4 mr-2" />}
            Transfer Ownership
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
